// In-composer voice surface (T18) — when a composer hosts voice, this strip
// REPLACES the text input in place (same shell, same gauge) instead of the
// full-screen VoiceOverlay stacking over the page.
//
// HOST-ANNOUNCEMENT CONTRACT (root.voiceHost — declared in canvas/state.js and
// workspace/state-app.js): 'canvas' | 'chat' | null — which composer currently
// OWNS the voice surface.
//   • The composer's VoiceBtn writes voiceHost synchronously (preventFetch) in
//     its onPointerdown, BEFORE voicePttPress — by the time the engine flips
//     voiceOpen the host is already known.
//   • While voiceOpen && voiceHost matches this strip's `hostKey` prop, the
//     strip is THE voice surface. The voiceMinimized axis is deliberately
//     ignored here: the strip is already composer-sized, so "minimized" is
//     meaningless while a composer hosts — this also keeps the engine's
//     talk-mode auto-minimize from ever leaving the session surfaceless.
//   • VoiceOverlay gates BOTH its postures on !voiceHost: full-screen at
//     voiceOpen && !voiceHost && !voiceMinimized, mini card at
//     voiceOpen && voiceMinimized && !voiceHost.
//   • Handing the session off to the island = CLEARING voiceHost (the shared
//     VoiceControls Type button writes { voiceMinimized: true,
//     voiceHost: null }); handing it to the call screen = voiceExpand, which
//     clears it too. voiceHost is never cleared on plain close — a stale
//     value is inert because every consumer AND-s it with voiceOpen, and every
//     open path re-asserts its own value.
//
// BECAUSE the strip is the surface users actually get (both mic buttons set a
// host), it must carry the FULL control set — stop-speaking, mute, expand and
// the hang-up included (2026-08-02 per Nika "add talk / command switcher - i
// dont see it": controls that lived only on the overlay were unreachable).
// The row is the SHARED call row (components/VoiceControls.js VoiceCallRow),
// identical in anatomy to the island's head; the words float above it in the
// SHARED bubble stack (_simone/AiBubbleStack.js), as they do over the island.
//
// Layout note (v3.14): `flow` is STATIC. Reactive layout props compute once at
// mount, so the strip is always a column and its rows show/hide instead.

export const VoiceStrip = {
  // FA204 / frank trap — hosting used to be a bare module-scope const read
  // from inside `display` (a serialized factory); it reads as undefined once
  // this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Moved into `scope`,
  // called via `el.scope.hosting`.
  scope: {
    // Is this strip instance the live voice host? Self-contained (no
    // cross-helper calls — the frank hoisted-helper constraint Chat.js
    // documents).
    hosting: (el, s) =>
      !!(s && s.root && s.root.voiceOpen && el.hostKey && s.root.voiceHost === el.hostKey)
  },
  // Mounted-closed root → reactive `display:` predicate, NOT `hide:` (house
  // display-gate rule — a hide:'d mounted-closed root sticks at display:none).
  // The whole subtree is gated here, so children never re-check hosting.
  display: (el, s) => (el.scope.hosting(el, s) ? 'flex' : 'none'),
  // "The morph should feel like ONE object changing state, not a swap"
  // (the ticket's own words). Before this, the swap between the typing UI
  // (Textarea/PromptButtons/CircleButton) and this strip was a hard
  // display:none↔flex cut with NO transition at all — the one moment in
  // this whole file family without one (every size/posture change around
  // it uses `transition: 'B all defaultBezier'`). display can't be
  // transitioned (a browser/CSS limit, not a DOMQL gap — this element is
  // legitimately "mounted-closed", see the comment above; switching its
  // gate to opacity/visibility to make it transitionable is the exact
  // anti-pattern that comment already warns off), so the fix is a genuine
  // CSS `animation` (design-system token, not a raw keyframe — same
  // `animation: 'fadeInUpShort'` seam already used elsewhere in this
  // codebase, e.g. tickets/pages/epics.js, chat/MessengerOverlay.js).
  // Unlike `transition`, an `animation` replays every time the element
  // re-enters the render tree, so it fires on every hosting cycle, not
  // just first mount. Short (opacity + ≤10%-ish rise), design-direction.md
  // §13's "single fade-rise" — the same restrained shape as VoiceOverlay's
  // own entrance. `fadeInUpShort` = brand/designSystem/animation.js
  // (translate3d 6.5%, opacity 0→1) — an existing registered token, not a
  // new keyframe.
  animation: 'fadeInUpShort',
  flow: 'y',
  align: 'stretch',
  width: '100%',
  minWidth: '0',
  // The anchor of the words above the bar (AiBubbleStack sits on this
  // strip's top edge, whatever positions the composer around it).
  position: 'relative',
  // The call's words float ABOVE the strip as bubbles. A host whose composer
  // box clips its overflow cannot float them — the assistant drawer's (Chat:
  // the brand prompt box is overflow hidden, and its thread is on screen and
  // shows the ask) — and sets `floatWords: false`: that strip keeps the newest
  // line in its row instead.
  floatWords: true,
  // Equal on every side — and on the resting pill this inset is what keeps
  // the orb and the hang-up concentric with the capsule's round ends (the
  // pill's half-height is the 36px control row's half plus this inset).
  padding: 'Y',

  // The call's 60 fps half — the orb's ring and the clock — only while THIS
  // strip is the call (two strips are mounted: the canvas prompt and the
  // assistant drawer's composer).
  onFrame: (el, s) => {
    if (!el.scope || !el.scope.hosting(el, s)) return
    el.call('voiceCallFrame')
  },

  // THE CALL BAR (2026-10-22, the call redesign) — the shared row: the orb,
  // the status word with the call's clock (over the hint, before anyone has
  // spoken), and the controls: [Try again | Stop] Type · Mute · Expand · End
  // call. One row at the control height: it never grows with the call.
  VoiceCallRow: {},

  // THE WORDS, AS BUBBLES ABOVE THE BAR (2026-10-28, Nika: "captions should be
  // like bubble and should not resize ai navbar") — the same stack the island
  // floats over its bar: the newest two lines of the call (the person's live
  // partial as it is heard), and a step that waits for the person's OK while
  // the thread is not on screen. Only where it can float (`floatWords`).
  AiBubbleStack: {
    if: (el) => !!el.parent && el.parent.floatWords !== false
  }
}
